import { useMemo, useRef, useState } from 'react';
import Button from '../buttons/button';
import { ButtonTheme } from '../buttons/button-theme';
import { Duration } from 'luxon';
import LoadingBar from '../loading/loading-bar';

export enum AudioSourceTypes {
  AudioMpeg = 'audio/mpeg'
}

const formatToTime = (seconds: number) => {
  const duration = Duration.fromObject({ seconds });

  if (duration.hours === 0) {
    return duration.toFormat('mm:ss');
  }

  return duration.toFormat('hh:mm:ss');
};

interface AudioNewProps {
  identifier: string;
  audioSource?: string;
  audioSourceType: AudioSourceTypes;
  isInitialising: boolean;
  onInitialise: () => void;
}

const AudioExternal = ({
  identifier,
  audioSource,
  audioSourceType,
  isInitialising,
  onInitialise
}: AudioNewProps) => {
  const audioRef = useRef<HTMLAudioElement | null>(null);

  const [isPlaying, setIsPlaying] = useState<boolean>(false);
  const [progress, setProgress] = useState<number>(0);
  const [totalDuration, setTotalDuration] = useState<number>(0);

  const togglePlayPause = () => {
    if (isPlaying) {
      audioRef.current?.pause();
      setIsPlaying(false);
    } else {
      void audioRef.current?.play();
      setIsPlaying(true);
    }
  };

  const onPlay = () => {
    if (!audioSource) {
      onInitialise();
    } else {
      togglePlayPause();
    }
  };

  const playbackProgressPercentage = useMemo(
    () =>
      totalDuration === 0 || progress === 0
        ? 0
        : (progress / totalDuration) * 100,
    [totalDuration, progress]
  );

  return (
    <>
      <div className="flex items-center border border-neutral-200 bg-white py-1 px-2 rounded">
        <Button
          onClick={onPlay}
          identifier={`play-${identifier}`}
          theme={ButtonTheme.CANCEL}
          isLoading={isInitialising}
          classOverrides="p-2"
        >
          <i
            className={`${
              isPlaying ? 'ra-icons-generic-pause' : 'ra-icons-generic-play'
            } flex`}
          ></i>
        </Button>
        <span className="text-sm mx-1">{formatToTime(progress)}</span>
        <div className="bg-neutral-500 rounded grow">
          <LoadingBar percentage={playbackProgressPercentage} />
        </div>
        <span className="text-sm ml-2">{formatToTime(totalDuration)}</span>
      </div>
      {audioSource ? (
        <audio
          className="hidden"
          controls
          autoPlay
          ref={audioRef}
          onDurationChange={(e) => setTotalDuration(e.currentTarget.duration)}
          onPlaying={() => setIsPlaying(true)}
          onPause={() => setIsPlaying(false)}
          onTimeUpdate={(e) => setProgress(e.currentTarget.currentTime)}
        >
          <source src={audioSource} type={audioSourceType} />
        </audio>
      ) : (
        <></>
      )}
    </>
  );
};

export default AudioExternal;
